import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Dropdown, Button, notification } from 'antd';
import { CustomImage, CustomPageHeader, CustomTable, Text, useText } from '@src/components';
import { useLang } from '@src/context';
import { formatPlate } from '@src/utils/utils';
import { CheckOutlined, MinusOutlined } from '@ant-design/icons';
import iconSetting from '@src/assets/icons/icon-settings.svg';
import icnUp from '@src/assets/icons/icn-up.svg';
import icnDown from '@src/assets/icons/icn-down.svg';
import smsTracking from '@src/assets/icons/sms-tracking.svg';
import filterButton from '@src/assets/icons/icon-task-square.svg';
import '@src/styles/demands/demands.scss';
import { getNewVehicleRequestList } from '@src/store/slice/newCarDemandsStore/newCarDemandsSlice';
import dayjs from '@src/utils/day';
import { debounce } from 'lodash/function';
import CustomIconComponent from '@src/components/CustomIconComponent';
import CustomFilterCollapse from '@src/components/CustomFilterCollapse';
import newCarDemandsService from '@src/services/newCarDemands/newCarDemands.services';
import { useHistory } from 'react-router-dom';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import CustomSearchBar from '@src/components/CustomSearchBar';
import CustomLoadingSpin from '@src/components/CustomLoadingSpin';
import CustomExpandRow from '@src/components/CustomExpandRow';


const getStatusClassName = (status) => {
  switch (status) {
    case 1:
      return 'demand-status-end-div';
    case 2:
      return 'demand-status-processing-div';
    case 3:
      return 'demand-status-process-div';
    case 4:
      return 'demand-status-cargo-div';
    case 5:
      return 'demand-status-failed-div';
    default:
      return 'demand-status-end-div';
  }
};

const daysState = (usableDays, i, day) => (
  <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
    <span className="new-car-demands-expand-days-text">{day}</span>
    {usableDays?.toString().substring(i, i + 1) === '1' ? (
      <div className="new-car-demands-expand-days-check">
        <CheckOutlined style={{ color: '#fff', fontSize: 10 }} />
      </div>
    ) : (
      <div className="new-car-demands-expand-days-minus">
        <MinusOutlined style={{ color: '#fff', fontSize: 10 }} />
      </div>
    )}
  </div>
);

const NewCarDemands = (props) => {
  const dispatch = useDispatch();
  const { language } = useLang();
  const {
    newCarDemands,
    orderOwnerList,
    statusList,
    providerList,
    deviceTypeList,
    loading,
    pagedProperty,
  } = useSelector((state) => state.newCarDemands);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);
  const [filteredText, setFilteredText] = useState('');
  const [searchedText, setSearchedText] = useState('');
  const [filteredPlateQuery, setFilteredPlateQuery] = useState('');
  const [requestor, setRequestor] = useState(null);
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState(null);
  const [providers, setProviders] = useState(null);
  const [checkedOwner, setCheckedOwner] = useState([]);
  const [checkedDevices, setCheckedDevices] = useState([]);
  const [checkedProviders, setCheckedProviders] = useState([]);
  const [filteredDate, setFilteredDate] = useState('');
  const [checkedStatus, setCheckedStatus] = useState([]);
  const [openSearchBar, setOpenSearchBar] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [panels, setPanels] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [isAvailableSendSMS, setIsAvailableSendSMS] = useState(true);
  const [disableTicketCarPage, setDisableTicketCarPage] = useState(true);
  const { menus } = useSelector((state) => state.menus);
  const inputRef = useRef(null);
  const [api, contextHolder] = notification.useNotification();
  const history = useHistory();
  const [columns, setColumns] = useState([]);
  const [requestFilter, setRequestFilter] = useState(null);
  const isMobil = useMediaQuery({ query: '(max-width: 768px)' });

  const ordersText = useText('orders');
  const sendSmsToDriver = useText('sendSmsToDriver');
  const plateText = useText('plate');

  const mondayText = useText('monday');
  const tuesdayText = useText('tuesday');
  const wednesdayText = useText('wednesday');
  const thursdayText = useText('thursday');
  const fridayText = useText('friday');
  const saturdayText = useText('saturday');
  const sundayText = useText('sunday');

  const shippingFollow = {
    title: <Text t="cargoTracking" />,
    dataIndex: 'cargoTrackingUrl',
    key: 'cargoTrackingUrl',
    align: 'left',
    width: 80,
    height: 50,
    render: (_, record) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row',
        }}
      >
        {record?.cargoTrackingUrl ? (
          <a
            href={record?.cargoTrackingUrl}
            target="_blank"
            style={{
              textAlign: 'center',
              paddingLeft: 10,
              flex: 1,
              textDecoration: 'underline',
            }}
          >
            <Text t="review" />
          </a>
        ) : (
          <span style={{ flex: 1, textAlign: 'center' }}>-</span>
        )}

        <CustomImage
          style={{ width: 24, height: 24, flexGrow: 0, cursor: 'pointer' }}
          src={expandedRowKeys?.includes(record.id) ? icnUp : icnDown}
          onClick={() => handleExpand(record.id)}
        />
      </div>
    ),
  };

  const providerCol = {
    title: <Text t="provider2" />,
    dataIndex: 'provider',
    key: 'provider',
    align: 'left',
    width: 70,
    height: 50,
    render: (_, record) => {
      const vouId = record.providerType;
      const IconComponent = CustomIconComponent({ providerType: vouId });
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {IconComponent && (
            <img
              src={IconComponent}
              alt={record.provider}
              style={{ marginLeft: 20, width: 24, height: 24 }}
            />
          )}
        </div>
      );
    },
  };

  const plateCol = {
    title: <Text t="licensePlate" />,
    dataIndex: 'plate',
    key: 'plate',
    align: 'left',
    width: 78,
    height: 50,
    className: 'custom-plate-column',
    render: (text) => formatPlate(text),
  };

  useEffect(() => {
    if (menus && !isAdmin) {
      if (menus.filter((item) => item.text?.includes('TICKET CAR')).length < 1) {
        setDisableTicketCarPage(false);
      }
    }
  }, [menus]);

  useEffect(() => {
    let tempColumns = [
      {
        title: '#',
        dataIndex: 'id',
        key: 'id',
        fixed: 'left',
        align: 'center',
        width: 30,
        height: 50,
        render: (_, record) => (
          <span
            className={`custom-id-column ${expandedRowKeys?.includes(record.id) ? 'new-car-demands-expand-circle' : ''
              }`}
          >
            {record?.id}
          </span>
        ),
        className: 'custom-id-column',
      },
    ];
    if (isMobil) {
      tempColumns = tempColumns.concat([plateCol, providerCol, shippingFollow]);
    } else {
      tempColumns = tempColumns.concat([
        {
          title: <Text t="orderNoUpperCase" />,
          dataIndex: 'orderNo',
          key: 'orderNo',
          align: 'left',
          width: 60,
          height: 50,
        },
        {
          title: <Text t="orderDate" />,
          dataIndex: 'orderTime',
          key: 'orderTime',
          align: 'left',
          width: 96,
          height: 50,
          render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm'),
        },
        plateCol,
        {
          title: <Text t="orderOwner2" />,
          dataIndex: 'orderOwner',
          key: 'orderOwner',
          align: 'left',
          width: 90,
          height: 50,
        },
        providerCol,
        {
          title: <Text t="deviceType4" />,
          dataIndex: 'deviceTypeLabel',
          key: 'deviceTypeLabel',
          align: 'left',
          width: 90,
          height: 50,
        },
      ]);
      if (isAdmin) {
        tempColumns = tempColumns.concat([
          {
            title: 'PO TICKET NO',
            dataIndex: 'ticketId',
            key: 'ticketId',
            align: 'left',
            width: 90,
            height: 50,
            render: (text) => (text === '0' || text === 0 ? '-' : text),
          },
        ]);
      }
      tempColumns = tempColumns.concat([
        {
          title: <Text t="status" />,
          dataIndex: 'statusDescription',
          key: 'statusDescription',
          align: 'left',
          width: isAdmin ? 120 : 90,
          height: 50,
          render: (_, record) => (
            <span
              className={getStatusClassName(record?.status)}
              style={{
                width: '100%',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isAdmin && record?.lastTicketResponse
                ? record?.statusDescription + ' - ' + record?.lastTicketResponse
                : record?.statusDescription}
            </span>
          ),
        },
        shippingFollow,
      ]);
    }
    setColumns(tempColumns);
  }, [isMobil, expandedRowKeys, isAdmin]);

  useEffect(() => {
    if (!disableTicketCarPage && history) {
      history.push('/dashboard');
    }
  }, [disableTicketCarPage]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [filteredText]);

  useEffect(() => {
    if (searchedText?.length > 2 && searchedText.replace(/\s+/g, '').length > 2) {
      const filter = { plate: searchedText.replace(/\s+/g, '') };
      clickFilter(filter);
      setFilteredPlateQuery(searchedText.replace(/\s+/g, ''));
    }
  }, [searchedText]);

  const fetchVehicleListData = async (flag = true) => {
    let req = { pageNumber: pagination.current, pageSize: pagination.pageSize };
    if (isAdmin && selectedUserId) {
      req.customerId = parseInt(selectedUserId, 10);
    }
    if (requestFilter && flag) {
      req.filter = requestFilter;
    }
    await dispatch(getNewVehicleRequestList(req));
  };

  useEffect(() => {
    fetchVehicleListData();
  }, [dispatch, pagination, selectedUserId, language]);

  useEffect(() => {
    try {
      if (orderOwnerList) {
        setRequestor(
          Object.entries(orderOwnerList).map(([key, value]) => ({
            key: key,
            value: value,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [orderOwnerList]);

  useEffect(() => {
    try {
      if (statusList) {
        setStatus(
          Object.entries(statusList).map(([key, value]) => ({
            key: key,
            value: value,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [statusList]);

  useEffect(() => {
    try {
      if (providerList) {
        setProviders(
          Object.entries(providerList).map(([key, value]) => ({
            key: key,
            value: value,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [providerList]);

  useEffect(() => {
    try {
      if (deviceTypeList) {
        setDevices(
          Object.entries(deviceTypeList).map(([key, value]) => ({
            key: key,
            value: value,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [deviceTypeList]);

  useEffect(() => {
    setPanels([
      {
        list: checkedOwner,
        setList: setCheckedOwner,
        text: <Text t="orderOwner3" />,
        initialList: requestor,
        key: 'owner',
      },
      {
        list: checkedDevices,
        setList: setCheckedDevices,
        text: <Text t="deviceType5" />,
        initialList: devices,
        key: 'devices',
      },
      {
        list: checkedProviders,
        setList: setCheckedProviders,
        text: <Text t="provider" />,
        initialList: providers,
        key: 'providers',
      },
      {
        setList: setFilteredDate,
        text: <Text t="orderDate2" />,
        key: 'orderTime',
        date: true,
        defaultValue: filteredDate,
      },
      {
        list: checkedStatus,
        setList: setCheckedStatus,
        text: <Text t="status3" />,
        initialList: status,
        key: 'status',
      },
    ]);
  }, [status, checkedOwner, checkedDevices, checkedProviders, checkedStatus, filteredDate]);

  const dataSourceWithId = newCarDemands.map((item, index) => ({
    ...item,
    id: index + 1, // index + 1 kullanarak sıfırdan başlamayan bir id ataması yapıyoruz
  }));

  const clearDemands = () => {
    try {
      setFilteredPlateQuery('');
      setRequestFilter(null);
      setCheckedOwner([]);
      setCheckedDevices([]);
      setCheckedProviders([]);
      setCheckedStatus([]);
      setFilteredText('');
      setSearchedText('');
      setFilteredDate('');
      fetchVehicleListData(false);
    } catch (error) {
      console.error(error);
    }
  };

  const clickFilter = async (tempFilter = null) => {
    try {
      const filter = tempFilter.plate
        ? tempFilter
        : {
          orderOwnerIds: checkedOwner?.map((it) => parseInt(it, 10)),
          deviceTypeIds: checkedDevices?.map((it) => parseInt(it, 10)),
          providerIds: checkedProviders?.map((it) => parseInt(it, 10)),
          statusIds: checkedStatus?.map((it) => parseInt(it, 10)),
          startTime: filteredDate?.start,
          endTime: filteredDate?.end,
        };
      let req = { pageNumber: 1, pageSize: pagination.pageSize, filter };
      if (isAdmin && selectedUserId) {
        req.customerId = parseInt(selectedUserId, 10);
      }
      req.filter = filter;
      setRequestFilter(filter);
      await dispatch(getNewVehicleRequestList(req));
    } catch (error) {
      console.error(error);
    }
  };

  const listenClickCheckbox = (setList, tempList) => {
    setList(tempList);
  };

  function FilterMenu() {
    return (
      <div className="custom-filter-menu">
        <div className="demands-custom-menu-div">
          <span className="custom-filter-menu-filtrele"><Text t="filter2" /></span>
          <button onClick={clearDemands} className="custom-filter-menu-clear">
            <Text t="clean" /></button>
        </div>

        <CustomFilterCollapse panels={panels} listenClickCheckbox={listenClickCheckbox} />

        <Button
          type="primary"
          onClick={clickFilter}
          block
          className="custom-filter-menu-filter-button"
        >
          <CustomImage src={filterButton} /> <Text t="filter2" />
        </Button>
      </div>
    );
  }

  const handlePageChange = (page, pageSize) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current: page?.current,
    }));
  };

  const sendSMS = async (id, provider) => {
    if (isAvailableSendSMS) {
      setIsAvailableSendSMS(false);
      const res = await newCarDemandsService.sendSmsDriver({ plate: id, providerType: provider });
      if (res?.isSuccess) {
        api.success({
          message: sendSmsToDriver,
          description: res.message,
          duration: 3,
        });
      }
      setTimeout(() => {
        setIsAvailableSendSMS(true);
      }, 120000);
    }
  };

  function expandedRowRender(record) {
    return (
      <>
        {isMobil && (
          <div className="new-car-demands-expand">
            <div className="vehicle-management-expand-line" style={{ height: 180 }} />
            <div
              className="vehicle-management-expand-main-div "
              style={{ marginBottom: 5, height: 160 }}
            >
              <CustomExpandRow width={90} title="SİPARİŞ NO:" text={record?.orderNo} />

              <CustomExpandRow
                width={90}
                title="SİPARİŞ TARİHİ:"
                text={dayjs(record?.orderTime).format('DD/MM/YYYY HH:mm')}
              />

              <CustomExpandRow width={90} title="SİPARİŞ SAHİBİ:" text={record?.orderOwner} />
              <CustomExpandRow width={90} title="CİHAZ TİPİ:" text={record?.deviceTypeLabel} />

              <div className="vehicle-management-expand-div">
                <span className="new-car-demands-expand-head" style={{ width: 90 }}>
                  <Text t="status" />:
                </span>
                <span
                  className={getStatusClassName(record?.status)}
                  style={{ flex: 1, width: '100%', textAlign: 'center' }}
                >
                  {record?.statusDescription}
                </span>
              </div>
            </div>
          </div>
        )}
        <div className="new-car-demands-expand">
          <div className="new-car-demands-expand-line" />
          <div className="new-car-demands-expand-div" style={{ flex: 0.7, maxWidth: 200 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                width: '100%',
                gap: isMobil ? 10 : 20,
              }}
            >
              <span className="new-car-demands-expand-head" style={{ flex: 1 }}>
                <Text t="limitType" />:
              </span>
              <span className="new-car-demands-expand-text">
                {record?.newVehicleRequestDetailResponseItem?.limitTypeLabel ?? '-'}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                width: '100%',
                gap: 20,
              }}
            >
              <span className="new-car-demands-expand-head" style={{ flex: 1 }}>
                <Text t="limitType4" />
              </span>
              <span className="new-car-demands-expand-text">
                {record?.newVehicleRequestDetailResponseItem?.limitAmount === 0 ||
                  record?.newVehicleRequestDetailResponseItem?.limitAmount === '0'
                  ? '-'
                  : record?.newVehicleRequestDetailResponseItem?.limitValueTypeLabel}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                width: '100%',
                gap: 20,
              }}
            >
              <span className="new-car-demands-expand-head" style={{ flex: 1 }}>
                <Text t="limitAmount" />
              </span>
              {record?.newVehicleRequestDetailResponseItem?.limitAmount === 0 ||
                record?.newVehicleRequestDetailResponseItem?.limitAmount === '0' ? (
                <span className="new-car-demands-expand-text">-</span>
              ) : (
                <span className="new-car-demands-expand-text">
                  {record?.newVehicleRequestDetailResponseItem?.limitAmount}{' '}
                  {record?.newVehicleRequestDetailResponseItem?.limitValueTypeLabel === 'Tutar'
                    ? '₺'
                    : 'lt.'}
                </span>
              )}
            </div>
          </div>
          <div className="new-car-demands-expand-div" style={{ flex: 1.9 }}>
            <span className="new-car-demands-expand-head" style={{ width: '100%' }}>
              <Text t="daysOpenForFuelPurchase" />
            </span>
            <div className="new-car-demands-expand-days-div" style={{ width: '100%' }}>
              {daysState(record?.newVehicleRequestDetailResponseItem?.usableDays, 0, mondayText)}
              {daysState(record?.newVehicleRequestDetailResponseItem?.usableDays, 1, tuesdayText)}
              {daysState(record?.newVehicleRequestDetailResponseItem?.usableDays, 2, wednesdayText)}
              {daysState(record?.newVehicleRequestDetailResponseItem?.usableDays, 3, thursdayText)}
              {daysState(record?.newVehicleRequestDetailResponseItem?.usableDays, 4, fridayText)}
              {daysState(record?.newVehicleRequestDetailResponseItem?.usableDays, 5, saturdayText)}
              {daysState(record?.newVehicleRequestDetailResponseItem?.usableDays, 6, sundayText)}
            </div>
          </div>
          {record?.deviceTypeId === 2 &&
            record?.newVehicleRequestDetailResponseItem?.orderNumber && (
              <div
                className="new-car-demands-expand-div"
                style={{ flex: 1.3, textAlign: 'center' }}
              >
                <span className="new-car-demands-expand-text"><Text t="turpakOrderNumber" /></span>
                <span className="new-car-demands-expand-head">
                  {record?.newVehicleRequestDetailResponseItem?.orderNumber}
                </span>
              </div>
            )}
          {record?.deviceTypeId === 2 &&
            record?.newVehicleRequestDetailResponseItem?.orderNumber &&
            record?.driverGsm && (
              <div className="new-car-demands-expand-div" style={{ flex: 1.1 }}>
                <button
                  className="new-car-demands-expand-sms-button"
                  onClick={() => sendSMS(record?.plate, record?.providerType)}
                >
                  <CustomImage style={{ width: 24, height: 24, flexGrow: 0 }} src={smsTracking} />
                  <span className="new-car-demands-expand-sms-text"><Text t="sendSmsToDriver" /></span>
                </button>
              </div>
            )}
        </div>
      </>
    );
  }

  const handleSearch = (value) => {
    setSearchedText(value);
  };

  const debouncedSearch = useCallback(debounce(handleSearch, 1000), []);

  const onInputChange = (e) => {
    const value = e.target.value;
    if (value?.length === 0) {
      try {
        if (filteredPlateQuery?.length > 1) {
          setFilteredPlateQuery('');
          fetchVehicleListData(false);
        }
      } catch (error) {
        console.error(error);
      }
      setOpenSearchBar(!openSearchBar);
    }
    setFilteredText(value);
    debouncedSearch(value);
  };

  const handleExpand = useCallback((rowKey) => {
    setExpandedRowKeys((expandedRows) => {
      if (expandedRows.includes(rowKey)) {
        return expandedRows.filter((id) => id !== rowKey);
      }
      return [...expandedRows, rowKey];
    });
  }, []);

  const content = () => {
    return loading.newCarDemands ? (
      <CustomLoadingSpin />
    ) : (
      <Card
        title={ordersText}
        className="balance-list-container"
        headStyle={{ fontFamily: 'UbuntuRegular', fontSize: '18px', fontWeight: 'bolder' }}
        extra={
          <div className="balance-tools" style={{ width: !openSearchBar ? 144 : 200 }}>
            {!openSearchBar && (
              <div className="header-notification" style={{ backgroundColor: 'transparent' }} />
            )}
            <CustomSearchBar
              placeholder={plateText}
              openSearchBar={openSearchBar}
              setOpenSearchBar={setOpenSearchBar}
              inputRef={inputRef}
              id="demandsFilterSearch"
              filteredText={filteredText}
              onInputChange={onInputChange}
            />

            <div className="header-notification">
              <Dropdown overlay={<FilterMenu />} trigger={['click']}>
                <Button
                  type="text"
                  icon={<CustomImage src={iconSetting} />}
                  className="demands-filter-image-button"
                />
              </Dropdown>
            </div>
          </div>
        }
      >
        {contextHolder}
        <CustomTable
          data-testid="table-test-id"
          pagination={
            pagedProperty?.totalItems < 11
              ? false
              : {
                pageSize: pagedProperty?.pageSize,
                total: pagedProperty?.totalItems,
                current: pagedProperty?.currentPage,
                className: 'demands-list-pagination',
              }
          }
          onPageChange={handlePageChange}
          dataSource={dataSourceWithId}
          columns={columns}
          className="babs-list-table"
          rowKey={(record) => record.id}
          expandedRowKeys={expandedRowKeys}
          expandable={{
            showExpandColumn: false,
            expandIconColumnIndex: 17,
            onExpand: (expanded, record, key) =>
              expandedRowKeys === key ? setExpandedRowKeys(null) : setExpandedRowKeys([key]),
            expandedRowRender: expandedRowRender,
          }}
        />
      </Card>
    );
  };

  return (
    <CustomPageHeader
      data-testid="demands-test-id"
      title={isAdmin ? null : <Text t="newVehicleRequests" />}
      showBreadCrumb={!isAdmin}
      showHelpButton={!isAdmin}
      adminCustomHeaderContent={isAdmin ? 'custom-header-content-subpage' : ''}
      routes={['ticketCarHeader']}
    >
      {content()}
    </CustomPageHeader>
  );
};

export default NewCarDemands;
